import { Routes, UrlMatchResult, UrlSegment } from "@angular/router";
import { AuthGuard, LayoutService } from "@core/services/common";
import { MainLayout } from "@layouts/main-layout";
import { OnboardingGuard } from "./core/services/common/onboarding.guard";
import { roleGuard } from "./core/services/common/role.guard";

const ADMIN_ROUTE = { roles: ["SYSTEM_ADMIN"] };
const OWNER_ROUTE = { roles: ["SYSTEM_ADMIN", "DATA_OWNER"] };
const MART_ROUTE = { roles: ["SYSTEM_ADMIN", "ADMIN_DATA_MART"] };
const ANY_USER = { roles: ["SYSTEM_ADMIN", "USER"] };

/** Match /data-explorer/pasutara and /pasutara/:schema/:tableName as one route. */
export function pasutaraMasterDetailMatcher(
  segments: UrlSegment[],
): UrlMatchResult | null {
  if (
    segments.length < 2 ||
    segments[0].path !== "data-explorer" ||
    segments[1].path !== "pasutara"
  ) {
    return null;
  }

  // Combined list: /data-explorer/pasutara
  if (segments.length === 2) {
    return { consumed: segments, posParams: {} };
  }

  // Selected table: /data-explorer/pasutara/:schema/:tableName
  if (segments.length === 4) {
    return {
      consumed: segments,
      posParams: {
        schema: segments[2],
        tableName: segments[3],
      },
    };
  }

  // Legacy schema-only URLs are handled by redirect routes.
  return null;
}

const DIRECT_SOURCE_SCHEMA_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

/** Match /data-explorer/:schema and /:schema/:tableName for any safe identifier except pasutara. */
export function directSourceMasterDetailMatcher(
  segments: UrlSegment[],
): UrlMatchResult | null {
  if (segments.length < 2 || segments[0].path !== "data-explorer") {
    return null;
  }

  const schema = segments[1].path;
  if (schema === "pasutara" || !DIRECT_SOURCE_SCHEMA_PATTERN.test(schema)) {
    return null;
  }

  // List: /data-explorer/:schema
  if (segments.length === 2) {
    return {
      consumed: segments,
      posParams: {
        schema: segments[1],
      },
    };
  }

  // Selected table: /data-explorer/:schema/:tableName
  if (segments.length === 3) {
    return {
      consumed: segments,
      posParams: {
        schema: segments[1],
        tableName: segments[2],
      },
    };
  }

  return null;
}

/** Only match /auth/* and /error/* so `/` is not swallowed by the public shell. */
export function publicShellMatcher(
  segments: UrlSegment[],
): UrlMatchResult | null {
  const first = segments[0]?.path
  if (first === "auth" || first === "error") {
    return { consumed: [] }
  }
  return null
}

export const routes: Routes = [
  {
    matcher: publicShellMatcher,
    canActivate: [LayoutService],
    children: [
      {
        path: "auth/sign-in",
        loadComponent: () =>
          import("./views/auth/sign-in/sign-in").then((m) => m.SignIn),
        data: { title: "เข้าสู่ระบบ" },
      },
      {
        path: "auth/sign-in/ldap",
        loadComponent: () =>
          import("./views/auth/ldap-sign-in/ldap-sign-in").then((m) => m.LdapSignIn),
        data: { title: "เข้าสู่ระบบด้วย DGR LDAP" },
      },
      {
        path: "auth/forgot-password",
        loadComponent: () =>
          import("./views/auth/forgot-password/forgot-password").then((m) => m.ForgotPassword),
        data: { title: "ลืมรหัสผ่าน" },
      },
      {
        path: "auth/reset-password",
        loadComponent: () =>
          import("./views/auth/reset-password/reset-password").then((m) => m.ResetPassword),
        data: { title: "ตั้งรหัสผ่านใหม่" },
      },
      {
        path: "auth/accept-terms",
        canActivate: [OnboardingGuard],
        loadComponent: () =>
          import("./views/auth/accept-terms/accept-terms").then((m) => m.AcceptTerms),
        data: { title: "เงื่อนไขการให้บริการ" },
      },
      {
        path: "auth/set-password",
        canActivate: [OnboardingGuard],
        loadComponent: () =>
          import("./views/auth/set-password/set-password").then((m) => m.SetPassword),
        data: { title: "ตั้งรหัสสำรองของระบบนี้" },
      },
      {
        path: "auth/change-password",
        canActivate: [OnboardingGuard],
        loadComponent: () =>
          import("./views/auth/change-password/change-password").then((m) => m.ChangePassword),
        data: { title: "เปลี่ยนรหัสผ่าน" },
      },
      // {
      //   path: 'auth/sign-up',
      //   loadComponent: () => import('./views/auth/sign-up/sign-up').then((m) => m.SignUp),
      //   data: { title: 'Sign Up' },
      // },
      // {
      //   path: 'auth/reset-pass',
      //   loadComponent: () => import('./views/auth/reset-pass/reset-pass').then((m) => m.ResetPass),
      //   data: { title: 'Reset Password' },
      // },
      // {
      //   path: 'auth/new-pass',
      //   loadComponent: () => import('./views/auth/new-pass/new-pass').then((m) => m.NewPass),
      //   data: { title: 'New Password' },
      // },
      // {
      //   path: 'auth/two-factor',
      //   loadComponent: () => import('./views/auth/two-factor/two-factor').then((m) => m.TwoFactor),
      //   data: { title: 'Two Factor' },
      // },
      // {
      //   path: 'auth/lock-screen',
      //   loadComponent: () => import('./views/auth/lock-screen/lock-screen').then((m) => m.LockScreen),
      //   data: { title: 'Lock Screen' },
      // },
      // {
      //   path: 'auth/success-mail',
      //   loadComponent: () => import('./views/auth/success-mail/success-mail').then((m) => m.SuccessMail),
      //   data: { title: 'Success Mail' },
      // },
      // {
      //   path: 'auth/login-pin',
      //   loadComponent: () => import('./views/auth/login-pin/login-pin').then((m) => m.LoginPin),
      //   data: { title: 'Login with PIN' },
      // },
      // {
      //   path: 'auth/delete-account',
      //   loadComponent: () => import('./views/auth/delete-account/delete-account').then((m) => m.DeleteAccount),
      //   data: { title: 'Delete Account' },
      // },
      // {
      //   path: 'auth/split/sign-in',
      //   loadComponent: () => import('./views/auth/split/sign-in/sign-in').then((m) => m.SignIn),
      //   data: { title: 'Sign In' },
      // },
      // {
      //   path: 'auth/split/sign-up',
      //   loadComponent: () => import('./views/auth/split/sign-up/sign-up').then((m) => m.SignUp),
      //   data: { title: 'Sign Up' },
      // },
      // {
      //   path: 'auth/split/reset-pass',
      //   loadComponent: () => import('./views/auth/split/reset-pass/reset-pass').then((m) => m.ResetPass),
      //   data: { title: 'Reset Password' },
      // },
      // {
      //   path: 'auth/split/new-pass',
      //   loadComponent: () => import('./views/auth/split/new-pass/new-pass').then((m) => m.NewPass),
      //   data: { title: 'New Password' },
      // },
      // {
      //   path: 'auth/split/two-factor',
      //   loadComponent: () => import('./views/auth/split/two-factor/two-factor').then((m) => m.TwoFactor),
      //   data: { title: 'Two Factor' },
      // },
      // {
      //   path: 'auth/split/lock-screen',
      //   loadComponent: () => import('./views/auth/split/lock-screen/lock-screen').then((m) => m.LockScreen),
      //   data: { title: 'Lock Screen' },
      // },
      // {
      //   path: 'auth/split/success-mail',
      //   loadComponent: () => import('./views/auth/split/success-mail/success-mail').then((m) => m.SuccessMail),
      //   data: { title: 'Success Mail' },
      // },
      // {
      //   path: 'auth/split/login-pin',
      //   loadComponent: () => import('./views/auth/split/login-pin/login-pin').then((m) => m.LoginPin),
      //   data: { title: 'Login with PIN' },
      // },
      // {
      //   path: 'auth/split/delete-account',
      //   loadComponent: () => import('./views/auth/split/delete-account/delete-account').then((m) => m.DeleteAccount),
      //   data: { title: 'Delete Account' },
      // },
      {
        path: "error/400",
        loadComponent: () =>
          import("./views/error/error-400/error-400").then((m) => m.Error400),
        data: { title: "400 Bad Request" },
      },
      {
        path: "error/401",
        loadComponent: () =>
          import("./views/error/error-401/error-401").then((m) => m.Error401),
        data: { title: "401 Unauthorized" },
      },
      {
        path: "error/403",
        loadComponent: () =>
          import("./views/error/error-403/error-403").then((m) => m.Error403),
        data: { title: "403 Forbidden" },
      },
      {
        path: "error/404",
        loadComponent: () =>
          import("./views/error/error-404/error-404").then((m) => m.Error404),
        data: { title: "404 Not Found" },
      },
      {
        path: "error/408",
        loadComponent: () =>
          import("./views/error/error-408/error-408").then((m) => m.Error408),
        data: { title: "408 Request Timeout" },
      },
      {
        path: "error/500",
        loadComponent: () =>
          import("./views/error/error-500/error-500").then((m) => m.Error500),
        data: { title: "500 Internal Server" },
      },
      {
        path: "error/maintenance",
        loadComponent: () =>
          import("./views/error/maintenance/maintenance").then(
            (m) => m.Maintenance,
          ),
        data: { title: "Maintenance" },
      },
    ],
  },
  {
    path: "",
    component: MainLayout,
    canActivate: [AuthGuard, LayoutService],
    canActivateChild: [roleGuard],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./views/pages/dashboard/dashboard").then((m) => m.Dashboard),
        data: { title: "ภาพรวม" },
      },
      {
        path: "dashboard",
        redirectTo: "",
        pathMatch: "full",
      },
      {
        path: "profile",
        loadComponent: () =>
          import("./views/pages/profile/profile").then((m) => m.Profile),
        data: { title: "บัญชีผู้ใช้งาน", ...ANY_USER },
      },
      {
        path: "no-access",
        loadComponent: () =>
          import("./views/pages/no-access/no-access").then((m) => m.NoAccess),
        data: { title: "ไม่มีสิทธิ์เข้าถึง", ...ANY_USER },
      },
      {
        path: "users",
        loadComponent: () =>
          import("./views/pages/users/users").then((m) => m.Users),
        data: { title: "จัดการผู้ใช้งาน", ...ADMIN_ROUTE },
      },
      {
        path: "metadata-databases",
        loadComponent: () =>
          import("./views/pages/data-sources/data-sources").then(
            (m) => m.DataSources,
          ),
        data: { title: "จัดการฐานข้อมูลต้นทาง", ...OWNER_ROUTE },
      },
      {
        path: "metadata-databases/:id/metadata-tables",
        loadComponent: () =>
          import("./views/pages/data-sources/data-sources-meta-tables").then(
            (m) => m.DataSourcesMetaTables,
          ),
        data: { title: "Meta Tables", ...OWNER_ROUTE },
      },
      {
        path: "metadata-change-logs",
        loadComponent: () =>
          import("./views/pages/metadata-change-logs/metadata-change-logs-list").then(
            (m) => m.MetadataChangeLogsList,
          ),
        data: { title: "ยืนยันโครงสร้างข้อมูล", ...OWNER_ROUTE },
      },
      {
        path: "metadata-change-logs/:metadataTableId",
        loadComponent: () =>
          import("./views/pages/metadata-change-logs/metadata-change-logs-detail").then(
            (m) => m.MetadataChangeLogsDetail,
          ),
        data: { title: "รายละเอียดการเปลี่ยนแปลงโครงสร้าง", ...OWNER_ROUTE },
      },
      {
        path: "metadata-databases/:id/data-owners",
        loadComponent: () =>
          import("./views/pages/data-sources/data-sources-owners").then(
            (m) => m.DataSourcesOwners,
          ),
        data: { title: "เจ้าของข้อมูล", ...ADMIN_ROUTE },
      },
      {
        path: "metadata-databases/:id/key-levels",
        loadComponent: () =>
          import("./views/pages/data-sources/data-sources-key-levels").then(
            (m) => m.DataSourcesKeyLevels,
          ),
        data: { title: "ระดับความลับรายสารเคมี", ...OWNER_ROUTE },
      },
      {
        path: "metadata-databases/:id/connection",
        loadComponent: () =>
          import("./views/pages/data-sources/data-sources-edit").then(
            (m) => m.DataSourcesEdit,
          ),
        data: { title: "แก้ไขการเชื่อมต่อ", ...OWNER_ROUTE },
      },
      {
        path: "metadata-databases/:id",
        redirectTo: (route) =>
          `/metadata-databases/${route.params["id"]}/connection`,
        pathMatch: "full",
      },
      {
        path: "etl-pipeline-scripts",
        loadComponent: () =>
          import("./views/pages/data-pipeline/data-pipeline").then(
            (m) => m.DataPipeline,
          ),
        data: { title: "จัดการประมวลผลข้อมูล", ...ADMIN_ROUTE },
      },
      {
        path: "etl-pipeline-scripts/:scriptId",
        loadComponent: () =>
          import("@/app/views/pages/data-pipeline/data-pipeline-detail").then(
            (m) => m.DataPipelineDetail,
          ),
        data: { title: "รายละเอียดสคริปต์", ...ADMIN_ROUTE },
      },
      {
        path: "etl-pipeline-scripts/:scriptId/edit",
        redirectTo: "etl-pipeline-scripts/:scriptId",
        pathMatch: "full",
      },
      {
        path: "data-explorer/pasutara/:schema",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        matcher: pasutaraMasterDetailMatcher,
        loadComponent: () =>
          import("./views/pages/pasutara/pasutara-list").then(
            (m) => m.PasutaraList,
          ),
        data: { title: "SMARTPASUTARA", ...OWNER_ROUTE },
      },
      {
        matcher: directSourceMasterDetailMatcher,
        loadComponent: () =>
          import("./views/pages/direct-source/direct-source-list").then(
            (m) => m.DirectSourceList,
          ),
        data: { title: "Direct Source", ...OWNER_ROUTE },
      },
      {
        path: "data-explorer/direct-sources",
        redirectTo: ({ queryParams }) => {
          const type = queryParams["type"];
          if (
            type === "BGEPA" ||
            type === "ELAB" ||
            type === "GCL" ||
            type === "TGMS"
          ) {
            return `data-explorer/${type}`;
          }
          return "data-explorer/BGEPA";
        },
        pathMatch: "full",
      },
      {
        path: "data-explorer/direct-sources/:schema",
        redirectTo: ({ params }) => {
          const schema = params["schema"];
          if (
            schema === "BGEPA" ||
            schema === "ELAB" ||
            schema === "GCL" ||
            schema === "TGMS"
          ) {
            return `data-explorer/${schema}`;
          }
          return "data-explorer/BGEPA";
        },
        pathMatch: "full",
      },
      {
        path: "data-explorer/direct-sources/:schema/:tableName",
        redirectTo: "data-explorer/:schema/:tableName",
      },
      {
        path: "datawarehouse-core/pasutara",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/pasutara/:schema",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/pasutara/:schema/:tableName",
        redirectTo: "data-explorer/pasutara/:schema/:tableName",
      },
      {
        path: "datawarehouse-core/direct-sources",
        redirectTo: "data-explorer/BGEPA",
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/direct-sources/:schema",
        redirectTo: ({ params }) => {
          const schema = params["schema"];
          if (
            schema === "BGEPA" ||
            schema === "ELAB" ||
            schema === "GCL" ||
            schema === "TGMS"
          ) {
            return `data-explorer/${schema}`;
          }
          return "data-explorer/BGEPA";
        },
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/direct-sources/:schema/:tableName",
        redirectTo: "data-explorer/:schema/:tableName",
      },
      {
        path: "datawarehouse-core/txns",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/txns/:tableName",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/mdms",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "datawarehouse-core/mdms/:tableName",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "data-explorer/txns",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "data-explorer/txns/:tableName",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "data-explorer/mdms",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "data-explorer/mdms/:tableName",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "datagroups",
        loadComponent: () =>
          import("./views/pages/data-groups/data-groups-list").then(
            (m) => m.DataGroupsList,
          ),
        data: { title: "จัดการกลุ่มชุดข้อมูล", ...ADMIN_ROUTE },
      },
      {
        path: "datagroups/:id",
        loadComponent: () =>
          import("./views/pages/data-groups/data-group-detail").then(
            (m) => m.DataGroupDetail,
          ),
        data: { title: "รายละเอียดกลุ่มชุดข้อมูล", ...ADMIN_ROUTE },
      },
      {
        path: "datasets",
        loadComponent: () =>
          import("./views/pages/datasets/datasets-list").then(
            (m) => m.DatasetsList,
          ),
        data: { title: "จัดการชุดข้อมูล", ...OWNER_ROUTE },
      },
      {
        path: "datasets/create",
        loadComponent: () =>
          import("./views/pages/datasets/dataset-builder/dataset-builder").then(
            (m) => m.DatasetBuilder,
          ),
        data: { title: "สร้างชุดข้อมูล", ...OWNER_ROUTE },
      },
      {
        path: "datasets/:id/edit/basic",
        redirectTo: "datasets/:id",
        pathMatch: "full",
      },
      {
        path: "datasets/:id/edit/structure",
        redirectTo: "datasets/:id",
        pathMatch: "full",
      },
      {
        path: "datasets/:id/edit",
        redirectTo: "datasets/:id",
        pathMatch: "full",
      },
      {
        path: "datasets/:id",
        loadComponent: () =>
          import("./views/pages/datasets/dataset-detail").then(
            (m) => m.DatasetDetail,
          ),
        data: { title: "รายละเอียดชุดข้อมูล", ...OWNER_ROUTE },
      },
      {
        path: "data-marts",
        loadComponent: () =>
          import("./views/pages/data-marts/data-marts-overview").then(
            (m) => m.DataMartsOverview,
          ),
        data: { title: "รายการคลังข้อมูลย่อย", ...MART_ROUTE },
      },
      {
        path: "data-marts/manage",
        redirectTo: "/data-marts",
        pathMatch: "full",
      },
      {
        path: "data-marts/create",
        loadComponent: () =>
          import("./views/pages/data-marts/data-marts-create").then(
            (m) => m.DataMartsCreate,
          ),
        data: { title: "สร้างคลังข้อมูลย่อย" },
      },
      {
        path: "data-marts/:id",
        loadComponent: () =>
          import("./views/pages/data-marts/data-mart-detail").then(
            (m) => m.DataMartDetail,
          ),
        data: { title: "รายละเอียดคลังข้อมูลย่อย", roles: ["SYSTEM_ADMIN", "ADMIN_DATA_MART", "DATA_OWNER"] },
      },
      {
        path: "data-mart-approvals",
        loadComponent: () =>
          import("./views/pages/data-marts/data-mart-approvals-list").then(
            (m) => m.DataMartApprovalsList,
          ),
        data: { title: "พิจารณาคำขอใช้ฟิลด์/ไฟล์ในคลังข้อมูลย่อย", ...OWNER_ROUTE },
      },
      {
        path: "data-mart-approvals/:requestId",
        loadComponent: () =>
          import("./views/pages/data-marts/data-mart-approval-detail").then(
            (m) => m.DataMartApprovalDetail,
          ),
        data: { title: "รายละเอียดคำขอใช้ฟิลด์", ...OWNER_ROUTE },
      },
      {
        path: "partner-access-requests",
        loadComponent: () =>
          import(
            "./views/pages/partner-access-requests/partner-access-requests-list"
          ).then((m) => m.PartnerAccessRequestsList),
        data: { title: "พิจารณาคำขอเข้าถึงข้อมูล", ...OWNER_ROUTE },
      },
      {
        path: "partner-access-requests/:requestId",
        loadComponent: () =>
          import(
            "./views/pages/partner-access-requests/partner-access-request-detail"
          ).then((m) => m.PartnerAccessRequestDetailPage),
        data: { title: "รายละเอียดคำขอเข้าถึงข้อมูล", ...OWNER_ROUTE },
      },
      // {
      //   path: "oauth-clients",
      //   loadComponent: () =>
      //     import("./views/pages/oauth-clients/oauth-clients-list").then(
      //       (m) => m.OauthClientsList,
      //     ),
      //   data: { title: "จัดการการเข้าถึง API (B2B)" },
      // },
      // {
      //   path: "oauth-clients/create",
      //   loadComponent: () =>
      //     import("./views/pages/oauth-clients/oauth-clients-create").then(
      //       (m) => m.OauthClientsCreate,
      //     ),
      //   data: { title: "ลงทะเบียนระบบเชื่อมต่อใหม่" },
      // },
      // {
      //   path: "oauth-clients/:id/view",
      //   loadComponent: () =>
      //     import("./views/pages/oauth-clients/oauth-clients-detail").then(
      //       (m) => m.OauthClientsDetail,
      //     ),
      //   data: { title: "รายละเอียดระบบเชื่อมต่อ" },
      // },
      // {
      //   path: "oauth-clients/:id",
      //   pathMatch: "full",
      //   redirectTo: ({ params }) => `/oauth-clients/${params["id"]}/view`,
      // },
      {
        path: "api-clients",
        loadComponent: () =>
          import("./views/pages/api-clients/api-clients-list").then(
            (m) => m.ApiClientsList,
          ),
        data: { title: "จัดการ API Key (B2B)", ...ADMIN_ROUTE },
      },
      {
        path: "api-clients/create",
        loadComponent: () =>
          import("./views/pages/api-clients/api-clients-create").then(
            (m) => m.ApiClientsCreate,
          ),
        data: { title: "ลงทะเบียนระบบเชื่อมต่อใหม่", ...ADMIN_ROUTE },
      },
      {
        path: "api-clients/:id/view",
        loadComponent: () =>
          import("./views/pages/api-clients/api-clients-detail").then(
            (m) => m.ApiClientsDetail,
          ),
        data: { title: "รายละเอียดระบบเชื่อมต่อ", ...ADMIN_ROUTE },
      },
      {
        path: "api-clients/:id",
        pathMatch: "full",
        redirectTo: ({ params }) => `/api-clients/${params["id"]}/view`,
      },
      {
        path: "data-pipeline",
        redirectTo: "etl-pipeline-scripts",
        pathMatch: "full",
      },
      {
        path: "data-pipeline/:scriptId",
        redirectTo: "etl-pipeline-scripts/:scriptId",
      },
      {
        path: "data-pipeline/:scriptId/edit",
        redirectTo: "etl-pipeline-scripts/:scriptId",
      },
      {
        path: "txn",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "txn/:tableName",
        redirectTo: "data-explorer/pasutara",
      },
      {
        path: "mdm",
        redirectTo: "data-explorer/pasutara",
        pathMatch: "full",
      },
      {
        path: "mdm/:tableName",
        redirectTo: "data-explorer/pasutara",
      },
    ],
  },
];
